// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * match.ts — N fields, one wave clock, and the spore traffic between them.
 *
 * This is the LOCAL orchestrator. It drives solo play (you plus bots) and it is
 * what `balance.test.ts` runs a few hundred times a second. In a live P2P room
 * the equivalent job is split — `session.ts` owns it, each peer runs exactly one
 * Game, and the host owns the wave clock — but the rules encoded here are the
 * same rules, which is why the balance numbers this produces are about the game
 * and not about the sim.
 *
 * The wave clock is deliberately the ONLY thing that couples the fields (plus
 * sends). Everything else is per-field and independent, which is what lets a
 * peer simulate itself and nothing else.
 */

import { Bot, type Policy } from './ai';
import { Game, type GameEvent } from './game';
import type { Mode } from './modes';

/** How often a bot re-plans. Not per tick — see ai.ts. */
export const BOT_DECIDE_MS = 1800;

export interface SeatConfig {
  name: string;
  /** Null for the human seat in solo play. */
  policy: Policy | null;
}

export interface MatchConfig {
  mode: Mode;
  seed: number;
  seats: SeatConfig[];
  /**
   * The seat a human is sitting in, if any. When that seat's Heart stops, the
   * MATCH stops — even though bots are still standing.
   *
   * Without this, solo play left the player watching a dead board forever: the
   * round only ended when one seat remained, so a human knocked out on wave 3 of
   * a three-way sat at Heart 0/16 with no results screen and no way forward.
   * That is precisely the "every player must reach the summary, never a frozen
   * board" failure — and it is invisible to the balance sim, which has no human
   * seat and correctly wants every match played to the last survivor.
   */
  humanSeat?: number;
}

export interface Standing {
  seat: number;
  name: string;
  alive: boolean;
  heart: number;
  wave: number;
  kills: number;
  leaked: number;
  sent: number;
  received: number;
  goldEarned: number;
  towers: number;
  peakRoad: number;
}

export class Match {
  readonly mode: Mode;
  readonly seed: number;
  readonly seats: SeatConfig[];
  readonly games: Game[];
  readonly bots: (Bot | null)[];
  readonly humanSeat: number | null;

  wave = 0;
  /** Ms since the current wave began. */
  waveMs = 0;
  elapsedMs = 0;
  over = false;
  /** Seat index of the winner, or -1 while it is still running. */
  winner = -1;

  /** Seat index -> the wave it died on, for the results table. */
  diedOnWave: number[];

  private botClock = 0;
  /** Sends routed this match, for the orphaned-spore audit. */
  sendLog: { from: number; to: number; wave: number }[] = [];

  constructor(config: MatchConfig) {
    this.mode = config.mode;
    this.seed = config.seed;
    this.seats = config.seats;
    this.games = config.seats.map(() => new Game(config.mode, config.seed));
    this.bots = config.seats.map((s, i) =>
      s.policy ? new Bot({ policy: s.policy, seed: `${config.seed}:bot${i}` }) : null,
    );
    this.humanSeat = config.humanSeat ?? null;
    this.diedOnWave = config.seats.map(() => -1);
    this.nextWave();
  }

  /** Turn on per-shot probes so the balance sim can audit targeting. */
  setAudit(on: boolean): void {
    for (const g of this.games) g.audit = on;
  }

  aliveSeats(): number[] {
    const out: number[] = [];
    for (let i = 0; i < this.games.length; i++) if (!this.games[i].over) out.push(i);
    return out;
  }

  /** The healthiest live rival's Heart, which is what catch-up income reads. */
  private bestRivalHeart(self: number): number {
    let best = 0;
    for (let i = 0; i < this.games.length; i++) {
      if (i === self || this.games[i].over) continue;
      if (this.games[i].heart > best) best = this.games[i].heart;
    }
    return best;
  }

  private nextWave(): void {
    this.wave++;
    this.waveMs = 0;
    if (this.wave > this.mode.waveCap) {
      this.finish();
      return;
    }
    for (let i = 0; i < this.games.length; i++) {
      const g = this.games[i];
      if (g.over) continue;
      if (this.wave > 1) g.payIncome(this.bestRivalHeart(i));
      g.startWave(this.wave);
    }
  }

  /**
   * Route a spore. Returns true when it actually landed — the balance sim's
   * `orphanedSpores` audit compares the number of sends the bots paid for
   * against the number of creeps that appeared, at zero tolerance.
   */
  send(from: number, to: number): boolean {
    const target = this.games[to];
    if (!target || target.over || to === from) return false;
    target.receive('spore');
    this.sendLog.push({ from, to, wave: this.wave });
    return true;
  }

  step(dtMs: number): void {
    if (this.over) return;
    this.elapsedMs += dtMs;
    this.waveMs += dtMs;

    for (const g of this.games) g.step(dtMs);

    this.botClock += dtMs;
    if (this.botClock >= BOT_DECIDE_MS) {
      this.botClock -= BOT_DECIDE_MS;
      this.runBots();
    }

    for (let i = 0; i < this.games.length; i++) {
      if (this.games[i].over && this.diedOnWave[i] < 0) this.diedOnWave[i] = this.wave;
    }

    // The human's Heart stopping ends the match, even with bots still standing —
    // otherwise the player watches a board they are no longer in. A one-seat
    // match is the same rule: it ends when that Heart stops, not when they are
    // "the last one standing", which they always were.
    const alive = this.aliveSeats();
    const humanOut = this.humanSeat !== null && this.games[this.humanSeat]?.over;
    if (humanOut || (this.games.length === 1 ? alive.length === 0 : alive.length <= 1)) {
      this.finish();
      return;
    }

    if (this.waveMs >= this.mode.waveMs) this.nextWave();
  }

  private runBots(): void {
    const hearts = this.games.map((g) => (g.over ? 0 : g.heart));
    for (let i = 0; i < this.bots.length; i++) {
      const bot = this.bots[i];
      const g = this.games[i];
      if (!bot || g.over) continue;
      const sends = bot.act(g);
      for (let s = 0; s < sends; s++) {
        const target = bot.pickTarget(hearts, i);
        if (target >= 0) this.send(i, target);
      }
    }
  }

  private finish(): void {
    if (this.over) return;
    this.over = true;
    const table = this.standings();
    this.winner = table.length ? table[0].seat : -1;

    // A human knocked out has lost, whoever else is still standing. Without
    // this, dying on wave 3 while two bots fought on could still rank the
    // player top of a table sorted by Heart-left and declare them the winner.
    if (this.humanSeat !== null && this.games[this.humanSeat]?.over && this.winner === this.humanSeat) {
      const survivor = table.find((r) => r.seat !== this.humanSeat && r.alive);
      this.winner = survivor ? survivor.seat : -1;
    }

    /**
     * A genuine dead heat is a DRAW, not a win for the lower seat.
     *
     * `standings()` has to end its comparator on something total, and seat index
     * is the only stable candidate — but that made seat order the tie-break for
     * the match itself. In Siege the horde is heavy enough that the last players
     * frequently die in the same tick with identical Hearts, so that tie-break
     * fired often, and it fired one way: over 360 matches across three
     * independent seed families, seat 0 won ~29% of 4-player Siege and seat 3
     * ~18%, against 25% chance. Replicated in every family, so not noise.
     *
     * Declaring the draw is the honest fix. Handing it to seat 0 was inventing a
     * winner; handing it to a random seat would only hide the bias behind an
     * rng. Draws are reported and excluded from win-rate statistics.
     */
    if (table.length > 1) {
      const [a, b] = table;
      const dead = !a.alive && !b.alive;
      if (dead && a.heart === b.heart && a.wave === b.wave && a.kills === b.kills) this.winner = -1;
    }

    // Nobody "wins" a solo run; the wave you reached is the score.
    if (this.games.length === 1) this.winner = this.games[0].over ? -1 : 0;
  }

  /**
   * Ranked outcome. Alive beats dead, then Heart beats left, then the wave you
   * died on, then kills. Every field is included, always — principle #9's "every
   * player's result, every time" is a property of this function returning the
   * whole table rather than a winner.
   */
  standings(): Standing[] {
    const rows: Standing[] = this.games.map((g, i) => ({
      seat: i,
      name: this.seats[i].name,
      alive: !g.over,
      heart: g.heart,
      wave: g.over && this.diedOnWave[i] > 0 ? this.diedOnWave[i] : this.wave,
      kills: g.stats.kills,
      leaked: g.stats.leaked,
      sent: g.stats.sent,
      received: g.stats.received,
      goldEarned: g.stats.goldEarned,
      towers: g.towers.size,
      peakRoad: g.stats.peakRoad,
    }));
    return rows.sort(
      (a, b) =>
        Number(b.alive) - Number(a.alive) ||
        b.heart - a.heart ||
        b.wave - a.wave ||
        b.kills - a.kills ||
        a.seat - b.seat,
    );
  }

  /** Drain every field's event stream. The sim audits this, then discards it. */
  drainEvents(): { seat: number; ev: GameEvent }[] {
    const out: { seat: number; ev: GameEvent }[] = [];
    for (let i = 0; i < this.games.length; i++) {
      for (const ev of this.games[i].events) out.push({ seat: i, ev });
      this.games[i].events = [];
    }
    return out;
  }
}
